import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { ISiteMapEquipment, ISiteMapPoint } from '@interfaces/ISiteMap';
import { clampToPlan, distance, screenToPlan } from './siteMap.logic';
import type { useSiteMapData } from './useSiteMapData';

type EquipmentDrag =
  | { kind: 'move'; equipmentId: string; point: ISiteMapPoint }
  | { kind: 'radius'; equipmentId: string; radius: number };

interface IUseEquipmentEditingArgs {
  equipment: ISiteMapEquipment[];
  overlayRef: RefObject<SVGSVGElement | null>;
  updateEquipment: ReturnType<typeof useSiteMapData>['updateEquipment'];
  removeEquipment: ReturnType<typeof useSiteMapData>['removeEquipment'];
}

/**
 * Owns post-create equipment editing (Task 9 §4.2): dragging a radius-edge
 * handle or the chip itself, and the remove confirm. Pointer math stays in
 * plan space via `screenToPlan`, read off the stage's own overlay SVG (passed
 * in as `overlayRef` — `MapStage` forwards its internal ref for exactly this).
 * `pointerdown` seeds the drag from the SAVED equipment (never from stale
 * closure state); `pointermove`/`pointerup` listen at the window level so the
 * drag survives the pointer leaving the small hit target, and end in a single
 * `updateEquipment` patch call.
 *
 * `dragRef` mirrors `drag` state and is the ONLY thing `onMove`/`onUp` read —
 * both handlers are set up once per drag (the effect is deliberately keyed on
 * identity only, see below) so a stale closure over `drag` would see the
 * value from the drag's first frame forever. Critically, `onUp`'s `.mutate()`
 * call reads `dragRef.current` directly rather than living inside a `setDrag`
 * functional updater: React (under StrictMode, which this app renders under)
 * double-invokes functional updaters in dev to catch exactly this — an
 * updater that fires a side effect would PATCH twice per drag.
 */
export function useEquipmentEditing({ equipment, overlayRef, updateEquipment, removeEquipment }: IUseEquipmentEditingArgs) {
  const [drag, setDrag] = useState<EquipmentDrag | null>(null);
  const dragRef = useRef<EquipmentDrag | null>(null);
  // Whether the pointer has actually moved since this drag's `pointerdown` —
  // `onUp` only mutates when it has, so a plain click (down+up, no move in
  // between) on a chip or a radius handle doesn't PATCH unchanged values.
  const movedRef = useRef(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const activeId = drag?.equipmentId ?? null;
  const activeKind = drag?.kind ?? null;

  useEffect(() => {
    if (!activeId || !activeKind) return;
    const original = equipment.find((eq) => eq.equipmentId === activeId);
    if (!original) return;

    const onMove = (e: PointerEvent) => {
      const svg = overlayRef.current;
      if (!svg) return;
      const raw = screenToPlan(svg, e.clientX, e.clientY);
      if (!raw) return;
      const p = clampToPlan(raw);
      movedRef.current = true;
      const cur = dragRef.current;
      if (!cur) return;
      const next: EquipmentDrag =
        cur.kind === 'move' ? { ...cur, point: p } : { ...cur, radius: Math.max(1, distance(original.position, p)) };
      dragRef.current = next;
      setDrag(next);
    };
    const onUp = () => {
      const cur = dragRef.current;
      if (cur && movedRef.current) {
        if (cur.kind === 'move') {
          updateEquipment.mutate({ equipmentId: cur.equipmentId, patch: { position: cur.point } });
        } else {
          updateEquipment.mutate({ equipmentId: cur.equipmentId, patch: { radius: cur.radius } });
        }
      }
      dragRef.current = null;
      movedRef.current = false;
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // Deliberately keyed only on the drag's identity, not its live point/
    // radius — those mutate every move via setDrag above, and re-subscribing
    // per-frame would tear down/re-add the window listeners constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, activeKind]);

  const displayEquipment = equipment.map((eq) => {
    if (!drag || drag.equipmentId !== eq.equipmentId) return eq;
    return drag.kind === 'move' ? { ...eq, position: drag.point } : { ...eq, radius: drag.radius };
  });

  // Two direct handlers rather than a `(kind) => (id, e) => …` curry — the
  // factory shape reads as "a function returned from a call made during
  // render" to the `react-hooks/refs` lint rule, which then can't prove the
  // ref write inside only ever runs from the later pointerdown event (not
  // render) and flags it as unsafe.
  const startDrag = (kind: EquipmentDrag['kind'], equipmentId: string, e: ReactPointerEvent) => {
    e.stopPropagation();
    const eq = equipment.find((x) => x.equipmentId === equipmentId);
    if (!eq) return;
    const next: EquipmentDrag =
      kind === 'move' ? { kind, equipmentId, point: eq.position } : { kind, equipmentId, radius: eq.radius };
    dragRef.current = next;
    movedRef.current = false;
    setDrag(next);
  };
  const onChipPointerDown = (equipmentId: string, e: ReactPointerEvent) => startDrag('move', equipmentId, e);
  const onRadiusPointerDown = (equipmentId: string, e: ReactPointerEvent) => startDrag('radius', equipmentId, e);

  return {
    displayEquipment,
    onChipPointerDown,
    onRadiusPointerDown,
    removingId,
    requestRemove: setRemovingId,
    cancelRemove: () => setRemovingId(null),
    confirmRemove: () => {
      if (removingId) removeEquipment.mutate(removingId);
      setRemovingId(null);
    },
  };
}
